import type { PuppyPackageParseResult } from './types';
import { resolveImportedTopologyBodyOrdinal } from '../../imported-model/utils';

/**
 * 恢复旧离线包的共享 Body 定位，只使用装配所有权和有序几何绑定，不解析显示名称。
 * 新包明确声明完整 geometry-object Shape 时不生成选择器；不同 occurrence 的序号独立。
 */
export class PuppyBrepBodySelectionResolver {
  /** 旧容器从 v6 开始保留恢复所需的几何绑定顺序。 */
  private static readonly minimumVersion = 6;

  /**
   * 生成 geometryObjectId 到局部 Body 序号的映射。
   * @param parsed 已通过完整协议与摘要校验的离线包。
   * @returns 需要从共享 BRep 提取的条目；独立几何和旧版缺少依据的条目不包含在内。
   */
  public resolve(parsed: PuppyPackageParseResult): Map<string, number> {
    const result = new Map<string, number>();
    if (parsed.manifest.version < PuppyBrepBodySelectionResolver.minimumVersion) return result;
    const entries = new Map(parsed.brepIndex.entries.map(entry => [entry.geometryObjectId, entry]));
    const sourceOrdinals = new Map<string, number>();
    // 同一 Body 的多个 occurrence 可以采用不同显示顺序，必须优先保留源拓扑协议编号。
    for (const asset of parsed.topologyRuntime.assets) {
      const ordinal = resolveImportedTopologyBodyOrdinal(asset.bodyId);
      if (ordinal === undefined) continue;
      const previous = sourceOrdinals.get(asset.geometryObjectId);
      if (previous !== undefined && previous !== ordinal) {
        throw new Error(`离线包源拓扑 Body 序号冲突: geometryObjectId=${asset.geometryObjectId}`);
      }
      sourceOrdinals.set(asset.geometryObjectId, ordinal);
    }
    // 零件文档只有一个定义作用域；装配文档则按直接拥有几何的 occurrence 独立恢复。
    const scopes = parsed.manifest.documentType === 'part'
      ? [parsed.assembly.nodes.flatMap(node => node.geometryObjectIds)]
      : parsed.assembly.nodes.map(node => node.geometryObjectIds);
    for (const ids of scopes) {
      const groups = new Map<string, Set<string>>();
      for (const id of ids) {
        const entry = entries.get(id);
        if (!entry || entry.shapeScope === 'geometry-object') continue;
        let group = groups.get(entry.brepSha256);
        if (!group) { group = new Set<string>(); groups.set(entry.brepSha256, group); }
        group.add(id);
      }
      for (const group of groups.values()) {
        // 仅跨 occurrence 的 SHA 重复不证明源包含多个 Body，不能据此选择第一个子体。
        if (group.size < 2) continue;
        let ordinal = 0;
        for (const id of group) {
          const selectedOrdinal = sourceOrdinals.get(id) ?? ordinal;
          const previous = result.get(id);
          if (previous !== undefined && previous !== selectedOrdinal) {
            throw new Error(`离线包同一几何对象的 Body 顺序冲突: geometryObjectId=${id}; first=${previous}; next=${selectedOrdinal}`);
          }
          result.set(id, selectedOrdinal);
          ordinal++;
        }
      }
    }
    return result;
  }
}
